import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Button, Select, Space, Typography, theme, Slider, notification } from 'antd'
import {
  SearchOutlined,
  CloseOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import { readSearchTypes } from '../../api/searchApi'
import GoogleAddressMobile from '../../../reusable-components/GoogleAddressMobile'
import { useSelector } from 'react-redux'
import { geocodeByAddress } from 'react-google-places-autocomplete'

const { Option } = Select
const { Title, Text } = Typography

const SearchRefinementMobile = ({
  onSearch,
  onFilterChange,
  searchFilters = {},
  loading = false,
  searchType,
  setSearchType,
  onClose,
  isFullScreen = false,
  hasSearched = false,
  isVirtualVisit = false,
  preSelectedServiceType = null,
  onBack
}) => {
  const [searchTypes, setSearchTypes] = useState([])
  const [address, setAddress] = useState(searchFilters.address || '')
  const [placeId, setPlaceId] = useState(searchFilters.placeId || '')
  const [location, setLocation] = useState(searchFilters.location || null)
  const [maxDistance, setMaxDistance] = useState(searchFilters.maxDistance || 25)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [hasManuallySetLocation, setHasManuallySetLocation] = useState(false)
  const [loadingStartTime, setLoadingStartTime] = useState(null)
  const [isUILoading, setIsUILoading] = useState(false)
  const { token } = theme.useToken()
  const patientAddress = useSelector((state) => state.auth.agiliteUser.residentialAddress)

  const updateLocationInRedux = useCallback(
    (updates) => {
      Object.entries(updates).forEach(([key, value]) => {
        onFilterChange?.(key, value)
      })
    },
    [onFilterChange]
  )

  useEffect(() => {
    fetchProfessionsSpecialties()
  }, [])

  // Track loading state with minimum animation time
  useEffect(() => {
    if (loading) {
      setLoadingStartTime(Date.now())
      setIsUILoading(true)
    } else if (loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime
      const minimumLoadingTime = 1000 // Reduced for mobile

      if (elapsed < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsed
        setTimeout(() => {
          setIsUILoading(false)
        }, remainingTime)
      } else {
        setIsUILoading(false)
      }
    }
  }, [loading, loadingStartTime])

  // Auto-set patient address as default location
  useEffect(() => {
    const setPatientLocation = async () => {
      if (patientAddress && !location && !hasManuallySetLocation && !isLoadingLocation) {
        setIsLoadingLocation(true)
        try {
          const results = await geocodeByAddress(patientAddress)

          if (results && results.length > 0) {
            const result = results[0]
            const newLocation = {
              address: patientAddress,
              place_id: result.place_id
            }

            setAddress(patientAddress)
            setPlaceId(result.place_id)
            setLocation(newLocation)

            updateLocationInRedux({
              address: patientAddress,
              placeId: result.place_id,
              location: newLocation
            })
          }
        } catch (error) {
          console.error('Error geocoding patient address:', error)
        } finally {
          setIsLoadingLocation(false)
        }
      }
    }

    setPatientLocation()
  }, [patientAddress, location, hasManuallySetLocation, isLoadingLocation, updateLocationInRedux])

  // Keep consultType aligned with visit mode
  useEffect(() => {
    // Entering virtual flow: ensure 'virtual' is set
    if (isVirtualVisit && (!searchFilters.consultType || searchFilters.consultType.length === 0)) {
      onFilterChange?.('consultType', ['virtual'])
      return
    }
    // Entering clinic/home flow: remove any lingering 'virtual'
    if (!isVirtualVisit && Array.isArray(searchFilters.consultType)) {
      const cleaned = searchFilters.consultType.filter((t) => t !== 'virtual')
      if (cleaned.length !== searchFilters.consultType.length) {
        onFilterChange?.('consultType', cleaned)
      }
    }
  }, [isVirtualVisit, searchFilters.consultType, onFilterChange])

  // Set searchType to virtual_consultation when in Virtual Visit mode, or general_practitioner for clinic visits
  useEffect(() => {
    if (isVirtualVisit && searchType !== 'virtual_consultation') {
      setSearchType('virtual_consultation')
    } else if (!isVirtualVisit && searchType === 'virtual_consultation') {
      // If not virtual visit but searchType is virtual_consultation, reset to general_practitioner
      setSearchType('general_practitioner')
    }
  }, [isVirtualVisit, searchType, setSearchType])

  // Initialize service type for virtual visits
  useEffect(() => {
    if (isVirtualVisit && preSelectedServiceType) {
      // If a service type is pre-selected, use it
      onFilterChange?.('serviceType', preSelectedServiceType)
    } else if (isVirtualVisit && !searchFilters.serviceType) {
      onFilterChange?.('serviceType', 'scheduled') // Default to scheduled
    }
  }, [isVirtualVisit, preSelectedServiceType, searchFilters.serviceType, onFilterChange])

  // Ensure contact method and reason start empty for virtual visits to show placeholders
  useEffect(() => {
    if (isVirtualVisit) {
      if (searchFilters.callType && searchFilters.callType !== '') {
        // Only reset if it has a value, to avoid infinite loops
        onFilterChange?.('callType', '')
      }
      if (searchFilters.appointmentReason && searchFilters.appointmentReason !== '') {
        // Only reset if it has a value, to avoid infinite loops
        onFilterChange?.('appointmentReason', '')
      }
    }
  }, [isVirtualVisit]) // Only run when isVirtualVisit changes

  const fetchProfessionsSpecialties = async () => {
    try {
      const tmpSearchTypes = await readSearchTypes()
      setSearchTypes(tmpSearchTypes)
    } catch (error) {}
  }

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (searchFilters.consultType?.length > 0) count += searchFilters.consultType.length
    if (searchFilters.timeSlot) count += 1
    if (searchFilters.languages?.length > 0) count += searchFilters.languages.length
    if (searchFilters.gender) count += 1
    if (searchFilters.serviceType) count += 1
    if (searchFilters.callType) count += 1
    if (searchFilters.appointmentReason) count += 1
    return count
  }, [searchFilters])

  const handleNewSearch = () => {
    // For virtual visits, validate required fields first
    if (isVirtualVisit) {
      if (!searchFilters.callType || !searchFilters.appointmentReason) {
        notification.warning({
          message: 'Required Fields Missing',
          description: 'Please select a contact method and reason for visit before searching.',
          duration: 3
        })
        return
      }
    }

    // For flexi and priority consultations, skip doctor search and go directly to booking
    if (isVirtualVisit && (searchFilters.serviceType === 'flexi' || searchFilters.serviceType === 'priority')) {
      // Navigate directly to booking confirmation for flexi/priority
      const flexiPriorityParams = {
        ...searchFilters,
        searchType,
        skipDoctorSelection: true,
        isFlexiPriority: true
      }

      console.log('🚀 Flexi/Priority - Skipping doctor search:', flexiPriorityParams)
      onSearch?.(flexiPriorityParams)
      return
    }

    const searchParams = {
      ...searchFilters,
      searchType,
      address,
      placeId,
      location,
      maxDistance
    }

    console.log('🔍 Mobile Search - Triggering search with params:', searchParams)
    console.log('🔍 Mobile Search - Loading should start now!')
    onSearch?.(searchParams)
  }

  const handleSearchTypeChange = (value) => {
    onFilterChange?.('searchType', value)

    const searchParams = {
      ...searchFilters,
      searchType: value,
      address,
      placeId,
      location,
      maxDistance
    }

    onSearch?.(searchParams)
  }

  const handleLocationChange = (value) => {
    setHasManuallySetLocation(true)
    if (value && value.value) {
      const newPlaceId = value.value.place_id
      const newAddress = value.label
      const newLocation = {
        address: newAddress,
        place_id: newPlaceId
      }

      setPlaceId(newPlaceId)
      setAddress(newAddress)
      setLocation(newLocation)

      updateLocationInRedux({
        placeId: newPlaceId,
        address: newAddress,
        location: newLocation
      })
    } else {
      setPlaceId('')
      setAddress('')
      setLocation(null)

      updateLocationInRedux({
        placeId: '',
        address: '',
        location: null
      })
    }
  }

  const clearAllFilters = () => {
    onFilterChange?.('consultType', [])
    onFilterChange?.('timeSlot', '')
    onFilterChange?.('languages', [])
    onFilterChange?.('gender', '')
    onFilterChange?.('searchType', 'general_practitioner')
    onFilterChange?.('serviceType', '')
    onFilterChange?.('callType', '')
    onFilterChange?.('appointmentReason', '')

    setAddress('')
    setPlaceId('')
    setLocation(null)
    setMaxDistance(25)
    setIsLoadingLocation(false)
    setHasManuallySetLocation(false)

    updateLocationInRedux({
      address: '',
      placeId: '',
      location: null,
      maxDistance: 25
    })
  }

  // Synchronize local state with Redux state when it changes
  useEffect(() => {
    if (searchFilters.address !== address) setAddress(searchFilters.address || '')
    if (searchFilters.placeId !== placeId) setPlaceId(searchFilters.placeId || '')
    if (JSON.stringify(searchFilters.location) !== JSON.stringify(location)) {
      setLocation(searchFilters.location || null)
    }
    if (searchFilters.maxDistance !== maxDistance) setMaxDistance(searchFilters.maxDistance || 25)
  }, [
    searchFilters.address,
    searchFilters.placeId,
    searchFilters.location,
    searchFilters.maxDistance,
    address,
    placeId,
    location,
    maxDistance
  ])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
          position: isFullScreen ? 'sticky' : 'static',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
              {isVirtualVisit
                ? 'Find Virtual Healthcare Professionals'
                : isFullScreen
                ? 'Find Healthcare Professionals'
                : 'Search Filters'}
            </Title>
            {activeFiltersCount > 0 && (
              <Text type='secondary' style={{ fontSize: '14px' }}>
                {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} applied
              </Text>
            )}
            {isFullScreen && !hasSearched && (
              <Text type='secondary' style={{ fontSize: '14px', display: 'block', marginTop: '4px' }}>
                {isVirtualVisit
                  ? 'Find doctors available for virtual consultations'
                  : 'Set your preferences to find the right doctor for you'}
              </Text>
            )}
          </div>
          {!isFullScreen && <Button type='text' icon={<CloseOutlined />} onClick={onClose} />}
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }} className='mobile-search-content'>
        {/* Back Button for Virtual Visits */}
        {isVirtualVisit && onBack && (
          <div style={{ marginBottom: '16px' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#6b7280',
                padding: '4px 8px',
                height: 'auto'
              }}
            >
              Back to Consultation Types
            </Button>
          </div>
        )}
        {/* Search Type Selector - Only show if not in Virtual Visit mode */}
        {!isVirtualVisit && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
              What are you looking for?
            </Text>
            <Select
              value={searchType}
              onChange={handleSearchTypeChange}
              style={{ width: '100%' }}
              size='large'
              placeholder='Select profession type'
              disabled={isUILoading}
            >
              {searchTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Service Type Selection - Hide for Virtual Visits since it's already chosen */}
        {!isVirtualVisit && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
              <ClockCircleOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Select Service Type
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                {
                  value: 'scheduled',
                  label: 'Scheduled Consultation',
                  description: 'Book a specific time slot with your selected doctor',
                  icon: '📅'
                },
                {
                  value: 'flexi',
                  label: 'Flexi Consultation',
                  description: 'Doctor will contact you during the course of the day',
                  icon: '🕐'
                },
                {
                  value: 'priority',
                  label: 'Priority Consultation',
                  description: 'Doctor will call within a few minutes',
                  icon: '🚨'
                }
              ].map((serviceType) => (
                <div
                  key={serviceType.value}
                  onClick={() => onFilterChange?.('serviceType', serviceType.value)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${
                      searchFilters.serviceType === serviceType.value ? token.colorPrimary : '#d9d9d9'
                    }`,
                    borderRadius: '8px',
                    backgroundColor: searchFilters.serviceType === serviceType.value ? '#f0f9ff' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${
                          searchFilters.serviceType === serviceType.value ? token.colorPrimary : '#d9d9d9'
                        }`,
                        backgroundColor: searchFilters.serviceType === serviceType.value ? token.colorPrimary : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '2px'
                      }}
                    >
                      {searchFilters.serviceType === serviceType.value && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text
                        strong
                        style={{
                          fontSize: '16px',
                          color: searchFilters.serviceType === serviceType.value ? token.colorPrimary : '#000'
                        }}
                      >
                        {serviceType.label}
                      </Text>
                      <div style={{ marginTop: '4px' }}>
                        <Text style={{ fontSize: '14px', color: '#666' }}>{serviceType.description}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call Type Selection - Only for Virtual Visits */}
        {isVirtualVisit && searchFilters.serviceType && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
              <VideoCameraOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Select Contact Method
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                {
                  value: 'video',
                  label: 'In App Video',
                  icon: '📹'
                },
                {
                  value: 'whatsapp',
                  label: 'WhatsApp Video',
                  icon: '📱'
                },
                {
                  value: 'phone',
                  label: 'Phone Call',
                  icon: '📞'
                }
              ].map((callType) => (
                <div
                  key={callType.value}
                  onClick={() => onFilterChange?.('callType', callType.value)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${searchFilters.callType === callType.value ? token.colorPrimary : '#d9d9d9'}`,
                    borderRadius: '8px',
                    backgroundColor: searchFilters.callType === callType.value ? '#f0f9ff' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${
                          searchFilters.callType === callType.value ? token.colorPrimary : '#d9d9d9'
                        }`,
                        backgroundColor: searchFilters.callType === callType.value ? token.colorPrimary : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {searchFilters.callType === callType.value && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />
                      )}
                    </div>
                    <Text
                      strong
                      style={{
                        fontSize: '16px',
                        color: searchFilters.callType === callType.value ? token.colorPrimary : '#000'
                      }}
                    >
                      {callType.label}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Appointment Reason - Only for Virtual Visits */}
        {isVirtualVisit && searchFilters.serviceType && searchFilters.callType && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
              <CalendarOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Reason for Visit
            </Text>
            <Select
              placeholder='Choose the reason for your visit...'
              value={searchFilters.appointmentReason}
              onChange={(value) => onFilterChange?.('appointmentReason', value)}
              style={{ width: '100%' }}
              size='large'
              disabled={isUILoading}
            >
              {[
                'General Consultation',
                'Follow-up Appointment',
                'Medical Certificate',
                'Prescription Renewal',
                'Mental Health Consultation',
                'Assessment',
                'Other'
              ].map((reason) => (
                <Option key={reason} value={reason}>
                  {reason}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Location - Only show for scheduled consultations or non-virtual visits */}
        {(!isVirtualVisit || searchFilters.serviceType === 'scheduled') && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
              <EnvironmentOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Location
            </Text>
            <GoogleAddressMobile
              selectProps={{
                placeholder: address || 'Enter your location',
                value: address ? { label: address, value: { place_id: placeId } } : null,
                onChange: handleLocationChange,
                isDisabled: isLoadingLocation || isUILoading,
                styles: {
                  control: (provided) => ({
                    ...provided,
                    minHeight: '48px',
                    fontSize: '16px'
                  })
                }
              }}
            />

            {location && (
              <div style={{ marginTop: '16px' }}>
                <Text style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                  Search within: <strong>{maxDistance} km</strong>
                </Text>
                <Slider
                  min={1}
                  max={50}
                  value={maxDistance}
                  onChange={(value) => {
                    setMaxDistance(value)
                    updateLocationInRedux({ maxDistance: value })
                  }}
                  marks={{
                    1: '1km',
                    25: '25km',
                    50: '50km'
                  }}
                  tooltip={{ formatter: (value) => `${value}km` }}
                  disabled={isUILoading}
                />
              </div>
            )}
          </div>
        )}

        {/* Type of Consultation - Only show if not in Virtual Visit mode */}
        {/* {!isVirtualVisit && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
              <VideoCameraOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Type of Consultation
            </Text>
            <Checkbox.Group
              style={{ width: '100%' }}
              value={searchFilters.consultType || []}
              onChange={(values) => handleFilterChange('consultType', values)}
              disabled={isUILoading}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Checkbox value='inPerson' style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <UserOutlined style={{ color: '#52c41a' }} />
                    <span>In-Person Consultation</span>
                  </Space>
                </Checkbox>
                <Checkbox value='virtual' style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <VideoCameraOutlined style={{ color: '#1890ff' }} />
                    <span>Virtual Consultation</span>
                  </Space>
                </Checkbox>
                <Checkbox value='homeVisit' style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <HomeOutlined style={{ color: '#fa8c16' }} />
                    <span>Home Visit</span>
                  </Space>
                </Checkbox>
              </div>
            </Checkbox.Group>
          </div>
        )} */}

        {/* Time Availability */}
        {/* <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            <ClockCircleOutlined style={{ marginRight: '8px', color: token.colorWarning }} />
            Time Availability
          </Text>
          <Radio.Group
            style={{ width: '100%' }}
            value={searchFilters.timeSlot}
            onChange={(e) => handleFilterChange('timeSlot', e.target.value)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Radio value='morning' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#fa8c16' }}>🌅</span>
                  <span>Morning (6AM - 12PM)</span>
                </Space>
              </Radio>
              <Radio value='afternoon' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#fadb14' }}>☀️</span>
                  <span>Afternoon (12PM - 6PM)</span>
                </Space>
              </Radio>
              <Radio value='evening' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#1e40af' }}>🌙</span>
                  <span>Evening (6PM - 12AM)</span>
                </Space>
              </Radio>
            </div>
          </Radio.Group>
        </div> */}

        {/* Languages */}
        {/* <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            <GlobalOutlined style={{ marginRight: '8px', color: token.colorSuccess }} />
            Languages
          </Text>
          <Checkbox.Group
            style={{ width: '100%' }}
            value={searchFilters.languages || []}
            onChange={(values) => handleFilterChange('languages', values)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { value: 'english', label: 'English', flag: '🇬🇧' },
                { value: 'afrikaans', label: 'Afrikaans', flag: '🇿🇦' },
                { value: 'zulu', label: 'Zulu', flag: '🇿🇦' },
                { value: 'xhosa', label: 'Xhosa', flag: '🇿🇦' },
                { value: 'sotho', label: 'Sotho', flag: '🇿🇦' }
              ].map((lang) => (
                <Checkbox key={lang.value} value={lang.value} style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </Space>
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </div> */}

        {/* Doctor Gender */}
        {/* <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            <TeamOutlined style={{ marginRight: '8px', color: token.colorInfo }} />
            Doctor Gender
          </Text>
          <Radio.Group
            style={{ width: '100%' }}
            value={searchFilters.gender}
            onChange={(e) => handleFilterChange('gender', e.target.value)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Radio value='male' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#1890ff' }}>👨‍⚕️</span>
                  <span>Male Doctor</span>
                </Space>
              </Radio>
              <Radio value='female' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#eb2f96' }}>👩‍⚕️</span>
                  <span>Female Doctor</span>
                </Space>
              </Radio>
              <Radio value='any' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span>👩‍⚕️👨‍⚕️</span>
                  <span>No Preference</span>
                </Space>
              </Radio>
            </div>
          </Radio.Group>
        </div> */}
      </div>

      {/* Sticky Footer with Actions */}
      <div
        style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff'
        }}
      >
        <Space style={{ width: '100%' }} direction='vertical' size='middle'>
          <Button
            type='primary'
            size='large'
            block
            icon={
              isVirtualVisit &&
              (searchFilters.serviceType === 'flexi' || searchFilters.serviceType === 'priority') ? null : (
                <SearchOutlined />
              )
            }
            onClick={handleNewSearch}
            loading={isUILoading}
            style={{ height: '48px', fontSize: '16px' }}
            disabled={isVirtualVisit && (!searchFilters.callType || !searchFilters.appointmentReason)}
          >
            {isVirtualVisit && (searchFilters.serviceType === 'flexi' || searchFilters.serviceType === 'priority')
              ? 'Continue to Payment'
              : 'Search Doctors'}
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              type='text'
              size='large'
              block
              icon={<ClearOutlined />}
              onClick={clearAllFilters}
              style={{ height: '40px' }}
            >
              Clear All Filters ({activeFiltersCount})
            </Button>
          )}
        </Space>
      </div>
    </div>
  )
}

export default SearchRefinementMobile
